import { useCallback, useEffect, useState } from "react";
import { Button, Card, IndexTable, Modal, Text } from "@shopify/polaris";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminPagedResult, AdminStoreSyncError } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const pageSize = 50;

export default function AdminErrorLogsPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState<AdminPagedResult<AdminStoreSyncError> | null>(null);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedErrorDetail, setSelectedErrorDetail] = useState<string | null>(null);

  const page = Math.max(1, Number(searchParams.get("page") || "1") || 1);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await agent.Admin.errorLogs(token, page, pageSize);
      setResult(response);
    } catch {
      setError("Unable to load error logs.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [token, page, onUnauthorized]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = result ? Math.max(1, Math.ceil(result.totalCount / result.pageSize)) : 1;

  const setPage = (nextPage: number) => {
    setSearchParams({ page: String(nextPage) });
  };


  const getTruncatedError = (value?: string) => {
    if (!value) return "(empty error)";
    return value.length > 55 ? `${value.slice(0, 55)}...` : value;
  };

  const clearErrorLogs = async () => {
    const confirmed = window.confirm("Clear all error log records?");
    if (!confirmed) return;

    setClearing(true);
    setError(null);
    try {
      await agent.Admin.clearErrorLogs(token);
      if (page !== 1) {
        setSearchParams({ page: "1" });
      } else {
        await loadLogs();
      }
    } catch {
      setError("Unable to clear error log records.");
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text as="h3" variant="headingMd">Error log details</Text>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button tone="critical" onClick={clearErrorLogs} loading={clearing}>Clear error log</Button>
            <Button onClick={loadLogs} loading={loading}>Refresh logs</Button>
          </div>
        </div>
        {error ? <Text as="p" tone="critical">{error}</Text> : null}
        {loading ? (
          <Text as="p">Loading error logs...</Text>
        ) : !result || result.items.length === 0 ? (
          <Text as="p">No error logs found.</Text>
        ) : (
          <IndexTable
            resourceName={{ singular: "error log", plural: "error logs" }}
            itemCount={result.items.length}
            selectable={false}
            headings={[
              { title: "ID" },
              { title: "Store" },
              { title: "Sync Log" },
              { title: "Object" },
              { title: "Time" },
              { title: "Error" },
            ]}
          >
            {result.items.map((row, index) => (
              <IndexTable.Row id={`${row.id}`} key={row.id} position={index}>
                <IndexTable.Cell>{row.id}</IndexTable.Cell>
                <IndexTable.Cell>
                  {row.shopId ? (
                    <Button variant="plain" onClick={() => navigate(`/pf-console/stores/${row.shopId}`)}>{row.shopName || row.shopDomain || `Shop ${row.shopId ?? "-"}`}</Button>
                  ) : (
                    <Text as="span">{row.shopName || row.shopDomain || "-"}</Text>
                  )}
                </IndexTable.Cell>
                <IndexTable.Cell>{row.shopifySyncLogId ?? "-"}</IndexTable.Cell>
                <IndexTable.Cell>{row.syncObject || "Unknown"} / {row.propertyName || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(row.logTime)}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Button
                    variant="plain"
                    tone="critical"
                    onClick={() => setSelectedErrorDetail(row.error || "(empty error)")}
                  >
                    {getTruncatedError(row.error)}
                  </Button>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        )}
        <Modal
          open={selectedErrorDetail !== null}
          onClose={() => setSelectedErrorDetail(null)}
          title="Error detail"
          primaryAction={{
            content: "Close",
            onAction: () => setSelectedErrorDetail(null),
          }}
        >
          <Modal.Section>
            <Text as="p" breakWord>
              {selectedErrorDetail || "(empty error)"}
            </Text>
          </Modal.Section>
        </Modal>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Previous</Button>
          <Text as="span">Page {page} of {totalPages}</Text>
          <Button disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </Card>
  );
}
